import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { addDays, format, parseISO, startOfDay } from 'date-fns'
import { CheckoutPickerField } from '../fields'
import { checkoutSchema } from '../schema'
import { MonoButton } from '../components'
import { monoHelperSx } from '../styles'
import { PositionCard } from '../PositionCard'
import type { CheckoutForm } from '../useCheckoutForm'

type Props = {
  form: CheckoutForm
  durationOptions: number[]
  skillByName: Map<string, { id: number; wage: number }>
  locked?: boolean
  onUnlock?: () => void
}

export const ShiftVerificationStep: React.FC<Props> = ({
  form,
  durationOptions,
  skillByName,
  locked = false,
  onUnlock,
}) => {
  const min = durationOptions[0] ?? 4
  const max = durationOptions[durationOptions.length - 1] ?? 10
  const today = React.useMemo(() => startOfDay(new Date()), [])
  const maxDate = React.useMemo(() => addDays(today, 30), [today])
  return (
    <>
      {locked && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            padding: '10px 12px',
            borderRadius: '3px',
            backgroundColor: 'warning.light',
            border: (t) => `1px solid ${t.palette.warning.main}`,
          }}
        >
          <LockIcon fontSize="small" sx={{ color: 'warning.dark' }} />
          <Typography sx={{ flex: 1, fontSize: 12, color: 'warning.dark' }}>
            Shift details are locked because your payment has already been
            authorized. Unlocking will release the authorization and require
            re-entering your card.
          </Typography>
          {onUnlock && (
            <MonoButton
              type="button"
              variant="text"
              size="small"
              onClick={onUnlock}
              sx={{ fontSize: 11, color: 'warning.dark', py: 0 }}
            >
              Unlock
            </MonoButton>
          )}
        </Box>
      )}

      <form.Subscribe selector={(s) => s.values.address}>
        {(address) => (
          <Box
            sx={{
              backgroundColor: 'grey.200',
              border: (t) => `1px solid ${t.palette.neutral.lightGray}`,
              borderRadius: '3px',
              padding: '10px 12px',
            }}
          >
            <Typography
              sx={{
                fontFamily: (theme) => theme.app.monoFontFamily,
                fontSize: 9,
                color: 'neutral.gray',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: '4px',
              }}
            >
              Shift Location
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'common.black' }}>
              {address || '—'}
            </Typography>
          </Box>
        )}
      </form.Subscribe>

      <form.Field
        name="date"
        validators={{ onChange: checkoutSchema.shape.date }}
      >
        {(field) => (
          <DatePicker
            label="Shift Date"
            format="MM/dd/yyyy"
            disablePast
            disabled={locked}
            minDate={today}
            maxDate={maxDate}
            value={field.state.value ? parseISO(field.state.value) : null}
            onChange={(v) =>
              field.handleChange(v ? format(v, 'yyyy-MM-dd') : '')
            }
            slots={{ textField: CheckoutPickerField }}
            slotProps={{
              textField: {
                id: 'date',
                onBlur: field.handleBlur,
                error: !field.state.meta.isValid,
                helperText: field.state.meta.errors[0]?.message,
              },
            }}
          />
        )}
      </form.Field>

      <Typography sx={[monoHelperSx, { mt: '-4px' }]}>
        To book shifts for multiple dates and access additional features, please
        use the Qwick platform.
      </Typography>

      <form.Field name="positions" mode="array">
        {(positionsField) => (
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {positionsField.state.value.map((pos, idx) => (
              <PositionCard
                key={`${pos.skillName}-${idx}`}
                form={form}
                idx={idx}
                skillName={pos.skillName}
                wage={skillByName.get(pos.skillName)?.wage}
                minDuration={min}
                maxDuration={max}
                locked={locked}
              />
            ))}
          </Stack>
        )}
      </form.Field>
    </>
  )
}
